/* state.js — the configurator design model + pure operations.
 *
 * Mirrors the engine's mutable globals (placements[], walls[], kitchenWizard) but as an
 * explicit `state` object, so React (or tests) own it instead of module globals. The
 * operations compose the other pure engine modules (snapping/validation). They mutate the
 * passed `state` (matching engine semantics) and return a small result; callers that want
 * immutability can clone first.
 *
 * Data models (METRES internally):
 *   wall:      { id, start:[x,z], end:[x,z], height, style?, thickness?, type? }
 *   placement: { id, type, wallId, absLeft, config?, faceSign?, island?, roomSensed? }
 */
import { defaultKitchenWizard } from "./wizardRules";
import { snapSides, wallInnerSpan, clampAbsLeftToSpan } from "./snapping";
import { validatePlacement } from "./validation";
import { getWallPlacementInfo } from "./placement";
import { clampToContact, snapExactFitEx } from "./contact";
import { footprintViolation } from "./containment";
import { dragMoveTo, commitMove } from "./drag";
import { makeWall, makeBox, makeArcFrom3, wallsForRoomShape } from "./walls";
import { commitSnapWall } from "./wallCleanup";
import { placeCabinetAt } from "./placementPipeline";
import { snapIslandXZ } from "./islandSnap";
import { quantMm } from "./precision";

let _idSeq = 0;
export function newPlacementId() {
  _idSeq += 1;
  return "pl_" + _idSeq + "_" + Math.round((_idSeq * 2654435761) % 1000);
}

export function createDesign(partial = {}) {
  return {
    productId: partial.productId || "kitchen",
    walls: partial.walls || [],
    placements: partial.placements || [],
    kitchenWizard: partial.kitchenWizard || defaultKitchenWizard(),
    currentStage: partial.currentStage || "walls",
    wallOpenings: partial.wallOpenings || [],
    symbolPlacements: partial.symbolPlacements || [],
    decor: partial.decor || [],
    floorPlan: partial.floorPlan || null, // custom floor polygon (3CAD Pavimenti); null = auto room loop
    // _native passthrough fields (furniture/product lines) — carried so a save never
    // drops them; buildNativeSnapshot null-coalesces to the engine's boot defaults.
    fabricId: partial.fabricId != null ? partial.fabricId : null,
    legId: partial.legId != null ? partial.legId : null,
    pillowsOn: partial.pillowsOn,
    headrestsOn: partial.headrestsOn,
    legHeight: partial.legHeight != null ? partial.legHeight : null,
  };
}

// ── Step 1: walls ──────────────────────────────────────────────────────────
/** 2026-07-19: walls drawn AFTER the real wizard inherit the Room step's
 *  thickness/height as drawing defaults (mm→m, wizard validation bounds).
 *  Projects without a wizard (or out-of-range values) keep the verbatim
 *  engine defaults — records stay field-free/byte-identical. */
function applyWizardWallDefaults(state, w) {
  // Gated on the REAL-wizard marker (state._wizardDefaults, set by the engine
  // hook when the loaded design carries a wizard snapshot / on in-session
  // completion; never serialized). createDesign()'s baked-in default wizard
  // must NOT stamp fields — legacy snapshots stay byte-identical.
  if (!state || !state._wizardDefaults) return w;
  const kw = (state && state.kitchenWizard) || {};
  if (typeof kw.wallHeight === "number" && kw.wallHeight >= 2000 && kw.wallHeight <= 4500) w.height = kw.wallHeight / 1000;
  if (typeof kw.wallThickness === "number" && kw.wallThickness >= 50 && kw.wallThickness <= 400) w.thickness = kw.wallThickness / 1000;
  return w;
}
/** ensureUniqueWallId(state, w): wall ids MUST be unique — wid()'s module
 *  counter resets on page reload, so a wall drawn after loading a saved design
 *  could reuse a persisted wall's id, and every id-addressed op (chain resync,
 *  corner binds, thickness, delete) would then hit the WRONG wall (the
 *  2026-08-21 "connecting one wall changes another wall" root cause). Existing
 *  walls keep their ids untouched; only the INCOMING wall is renamed, with a
 *  traceable "~n" suffix. */
export function ensureUniqueWallId(state, w) {
  if (!state || !Array.isArray(state.walls) || !w) return w;
  const ids = new Set();
  state.walls.forEach((x) => { if (x && x.id != null) ids.add(x.id); });
  if (!ids.has(w.id)) return w;
  const base = String(w.id);
  let n = 1;
  while (ids.has(base + "~" + n)) n++;
  w.id = base + "~" + n;
  return w;
}
/** Add a straight wall (Line/Chained). RAW semantics — no snapping (imports,
 *  applyRoomShape and the API must be able to place raw geometry deliberately). */
export function addWall(state, start, end) {
  const w = ensureUniqueWallId(state, applyWizardWallDefaults(state, makeWall(start, end)));
  state.walls.push(w);
  return w;
}
/** Stage W2: commit-snap variant for UI-committed walls — both endpoints pass
 *  through the wallCleanup pipeline (endpoint-cluster → end band → centerline
 *  projection, inclusive at jointTol()), then a plain addWall. Degenerate
 *  results fall back to the raw endpoints (commitSnapWall guard). */
export function addWallSnapped(state, start, end) {
  const r = commitSnapWall(start, end, state.walls);
  return addWall(state, r.start, r.end);
}
/** Add a rectangular room (Box) — four connected walls. */
export function addBox(state, c1, c2) {
  const ws = makeBox(c1, c2);
  ws.forEach((w) => state.walls.push(ensureUniqueWallId(state, applyWizardWallDefaults(state, w))));
  return ws;
}
/** Add a curved wall from start, end + through-point (3-click Arc). */
export function addArc(state, p0, p1, through) {
  const w = ensureUniqueWallId(state, applyWizardWallDefaults(state, makeArcFrom3(p0, p1, through)));
  state.walls.push(w);
  return w;
}
export function clearWalls(state) { state.walls.length = 0; }
/** Build the room from a wizard shape preset (replaces walls). (lhc-app.html applyRoomShape ~33614) */
export function applyRoomShape(state, shape, opts) {
  state.walls = wallsForRoomShape(shape, opts || {});
  return state.walls;
}
/** Per-wall thickness override (Stage W1, metres). Validation mirrors
 *  constants.setWallThickness (finite number > 0). Anything else DELETES the
 *  key, restoring the legacy field-free emission — saves of untouched walls
 *  stay byte-identical (serialization compat ruling: the field is persisted
 *  ONLY when set; makeWall never emits it). */
export function setWallThicknessFor(state, id, m) {
  const w = state.walls.find((x) => x.id === id);
  if (!w) return { ok: false, reason: "Unknown wall " + id };
  if (typeof m === "number" && isFinite(m) && m > 0) {
    w.thickness = m;
    return { ok: true, thickness: m };
  }
  delete w.thickness;
  return { ok: true, thickness: null };
}
export function removeWall(state, id) {
  const i = state.walls.findIndex((w) => w.id === id);
  if (i < 0) return { ok: false };
  state.walls.splice(i, 1);
  // drop placements that were on the removed wall
  state.placements = state.placements.filter((p) => p.wallId !== id);
  return { ok: true };
}

/** Add a cabinet to a wall at a given absLeft (clamped to the legal inner span). */
export function addCabinet(state, type, wallId, absLeft, lib) {
  const def = lib[type];
  if (!def) return { ok: false, reason: "Unknown module " + type };
  const wall = state.walls.find((w) => w.id === wallId);
  if (!wall) return { ok: false, reason: "Unknown wall " + wallId };
  const sp = wallInnerSpan(wall, state.walls);
  const lo = sp.off0;
  const hi = Math.max(lo, sp.len - sp.off1 - def.width);
  const clamped = Math.max(lo, Math.min(absLeft, hi));
  const p = { id: newPlacementId(), type, wallId, absLeft: clamped, roomSensed: true };
  // PRODUCT DEFAULT (2026-08-11 technician mandate): a new module starts as
  // the EXACT setup the technician saved on the bench (def.defaultConfig =
  // benchConfigOf over u.cfg) — the customer then adjusts within the
  // allow-lists. Deep copy: instances never share the def's object.
  if (def.defaultConfig) p.config = JSON.parse(JSON.stringify(def.defaultConfig));
  const v = validatePlacement(p, state.placements, lib, state.walls, state.kitchenWizard, state.wallOpenings);
  if (!v.ok) return { ok: false, reason: v.reason, placement: p };
  state.placements.push(p);
  return { ok: true, placement: p };
}

/** Move a cabinet to a new absLeft along its wall using the real contact stop, validate,
 *  revert on failure, then apply the neighbour/wall-end magnet.
 *  opts.exact (user mandate: NUMERIC INPUT BEATS SNAPPING) — skip the snapSides
 *  magnet entirely. Typed moves under SNAP_SIDE_MM (30mm) used to be silently
 *  pulled back to the edge they moved away from; with exact, 1mm moves 1mm. */
export function moveCabinet(state, id, newAbsLeft, lib, moveDir, opts = {}) {
  const p = state.placements.find((x) => x.id === id);
  if (!p) return { ok: false, reason: "No placement " + id };
  const def = lib[p.type];
  const wall = state.walls.find((w) => w.id === p.wallId);
  const info = wall ? getWallPlacementInfo(wall, state.walls) : null;
  const wLen = info ? info.len : 0;
  const orig = p.absLeft;
  // THE 4mm-gap bug lived here: Math.max(0, …) pinned the cabinet to the CENTERLINE
  // NODE. The legal minimum is the signed span floor (negative at a convex corner —
  // flush with the visible wall-band corner). Single clamp source: clampAbsLeftToSpan.
  const desired = wall && def
    ? clampAbsLeftToSpan(newAbsLeft, def.width, wall, state.walls)
    : Math.max(0, Math.min(newAbsLeft, wLen - (def ? def.width : 0)));
  const fromLeft = typeof orig === "number" ? orig : null;
  // interactive moves (keyboard nudge, elevation) auto-seat into a tight opening like the
  // plan drag does; an EXACT numeric move (typed value) lands where it was typed
  const fit = def && !opts.exact ? snapExactFitEx(p, def, desired, state.placements, lib, state.walls) : null;
  p.absLeft = fit && fit.seated ? fit.absLeft : (def ? clampToContact(p, def, desired, fromLeft, state.placements, lib, state.walls) : desired);
  const v = validatePlacement(p, state.placements, lib, state.walls, state.kitchenWizard, state.wallOpenings);
  if (!v.ok) { p.absLeft = orig; return { ok: false, reason: v.reason, absLeft: orig }; }
  if (!opts.exact) p.absLeft = snapSides(p, state.placements, lib, state.walls, moveDir);
  // CAD PRECISION (Issue 4/5): commits land on the 1mm grid — the typed
  // clearance/move value and the stored geometry can never disagree.
  p.absLeft = quantMm(p.absLeft);
  return { ok: true, absLeft: p.absLeft };
}

/** Drop at a world point (metres): the FULL drop/commit pipeline in the engine's call order
 *  (lhc-app.html 36527–36566 — snapSides(150) → snapToCornerRuns(0.15) → resolveWallOverlap
 *  → snapSides(40) → ruleAllowPlacement → push → maybeAutoCorner → appliance-picker hook →
 *  ruleAdvise), incl. v0.32 RULE B2: a drop farther than snapDist (default 0.7m) from every
 *  wall places nothing. Returns the pipeline result ({ ok, placement, reason, autoCorner,
 *  appliancePicker, advice, warning, toast }); opts.island for P1 island placement. */
export function dropCabinetAt(state, type, wx, wz, lib, snapDist, opts = {}) {
  return placeCabinetAt(state, type, [wx, wz], lib, { ...opts, snapDist });
}

export function removeCabinet(state, id) {
  const i = state.placements.findIndex((p) => p.id === id);
  if (i < 0) return { ok: false };
  state.placements.splice(i, 1);
  return { ok: true };
}

/** Drag a cabinet to a world point (metres): snap to nearest wall + contact-clamp, validate,
 *  revert on failure, apply magnet — the full interactive move, engine-side. */
export function dragCabinetTo(state, id, world, lib, snapDist, moveDir) {
  const p = state.placements.find((x) => x.id === id);
  if (!p) return { ok: false, reason: "No placement " + id };
  const def = lib[p.type];

  // ── ISLAND drag (2026-07-14 CAD mandate, Issue 1): free-standing units move
  // freely with the CAD magnet — 0mm edge-to-edge contact, flush and center
  // alignment against every neighbour — then validate. An invalid frame holds
  // the LAST VALID pose (same contract as the wall path). Coordinates commit
  // on the 1mm grid; `guides` feed the view's green alignment lines. ──
  if (p.island) {
    const last = { x: p.x, z: p.z };
    const snapped = snapIslandXZ(p, def, world[0], world[1], state.placements, lib, state.walls);
    p.x = quantMm(snapped.x);
    p.z = quantMm(snapped.z);
    const vI = validatePlacement(p, state.placements, lib, state.walls, state.kitchenWizard, state.wallOpenings);
    if (!vI.ok) {
      p.x = last.x; p.z = last.z;
      return { ok: false, reason: vI.reason, guides: snapped.guides };
    }
    p.roomSensed = true;
    return { ok: true, guides: snapped.guides };
  }

  const orig = { wallId: p.wallId, absLeft: p.absLeft };
  const target = dragMoveTo(p, def, world, state.walls, state.placements, lib, 0, snapDist);
  p.wallId = target.wallId;
  p.absLeft = target.absLeft;
  if (target.faceSign === null) delete p.faceSign;                 // ordinary wall — face no longer applies
  else if (target.faceSign !== undefined) p.faceSign = target.faceSign; // cursor side picks the face (peninsula)
  const r = commitMove(p, state.walls, state.placements, lib, state.kitchenWizard, state.wallOpenings, orig, moveDir);
  // NO GHOST MOVE (2026-07-15 user mandate): a wall cabinet NEVER detaches into
  // a free-floating island mid-drag — a refused frame simply holds the last
  // valid pose (commitMove already reverted). Cabinets only snap to walls.
  // v0.31a ROOM-SENSE (LH decision, _moveCommit ~38028): moving = re-placing — a moved
  // cabinet adopts the corrected orientation even when the commit reverted the position.
  p.roomSensed = true;
  // DRAG DEBUG (user-requested): every reference the pipeline used, so any future
  // hidden offset is visible immediately. Dev builds always; prod via window.__lhcDragDebug.
  if (typeof window !== "undefined" && (window.__lhcDragDebug || process.env.NODE_ENV === "development")) {
    const wall = state.walls.find((w) => w.id === p.wallId);
    const sp = wall ? wallInnerSpan(wall, state.walls) : null;
    const width = def ? def.width : 0;
    // eslint-disable-next-line no-console
    console.log("[lhc-drag]", {
      wallStart: wall ? wall.start : null,
      wallEnd: wall ? wall.end : null,
      cabinetOrigin: target.absLeft,               // absLeft IS the left physical face (m)
      cabinetLeft: r.absLeft,
      cabinetRight: r.absLeft + width,
      snappedX: target.absLeft,                    // after wall-snap + contact clamp
      clampedX: sp ? Math.max(sp.off0, Math.min(target.absLeft, sp.len - sp.off1 - width)) : target.absLeft,
      collisionOffset: r.absLeft - target.absLeft, // magnet/validation delta
      dimensionStart: sp ? sp.off0 : 0,            // chain origin = signed face corner
      dimensionEnd: sp ? sp.len - sp.off1 : 0,
      spanLo: sp ? sp.off0 : 0,                    // legal min (negative at a convex corner)
      spanHi: sp ? sp.len - sp.off1 - width : 0,   // legal max
      finalX: r.absLeft,
      roomContainment: def ? footprintViolation(p, def, state.walls) == null : true,
      ok: r.ok, reason: r.reason || null,
    });
  }
  return r;
}
